// ============================================================================
// AFB-0081 (Jose Azocar, 2026-08-17) — the two intake answers the board never
// showed back.
//
//   "The card when created does ask those questions but the summary is not
//    showing that so if I would like to confirm if a new account will be
//    Usables or at what management fee I am not able to see it"
//
// He is right, and it is worse than a missing summary line: `management_fee_pct`
// appeared NOWHERE in sop-detail.tsx, and `tcs_provides_usables` was not even in
// SopEditableField — so it was captured once at intake and then neither visible
// nor changeable anywhere in the app. AFB-0080, which he filed 81 seconds
// earlier and then corrected himself, reported the fields as missing; they were
// only invisible.
//
// ---------------------------------------------------------------------------
// THE THREE STATES, AND WHY 0 IS NOT MISSING
// ---------------------------------------------------------------------------
// management_fee_pct is a FRACTION (0.0300 = 3%) and, per ./mgmt-fee, intake
// NEVER writes NULL: the toggle off persists an explicit 0, because 0 is
// arithmetic-safe for downstream revenue x fee math. So on this column:
//
//     0.03  = answered, 3%
//     0     = answered, deliberately no fee   <- NOT a gap
//     NULL  = nobody ever answered            <- the gap
//
// Live proof that the distinction is load-bearing: SOP-0212 sits in the pipeline
// at 0.0000 having been answered on purpose. Treating 0 as missing would flag it
// forever; treating NULL as 0 would silently invent a fee of nothing.
//
// tcs_provides_usables is the AFB-0041 tri-state boolean: true / false / NULL,
// where NULL means unanswered and must never render as "No".
//
// ---------------------------------------------------------------------------
// WHY COMPLETED CARDS ARE MUTED (W-04, Ben's call 2026-08-18)
// ---------------------------------------------------------------------------
// The 25 cards with a NULL fee are ALL at stage Completed and all pre-date the
// 2026-07 intake work. Measured 2026-08-18:
//
//     stage                      cards   fee NULL   usables NULL
//     Completed                     27         25             27
//     Final_Pre_Invoicing_Check      1          0              1
//     Info_and_Sub_Checklist         8          0              3
//
// Every card in the live pipeline already has its fee. So a uniform
// "Not answered" would light up 27 finished historical cards as though something
// were wrong — which reads as a new bug and generates the next ticket. Missing
// answers are therefore ACTION-REQUIRED while a card is still moving and MUTED
// once it is Completed: same fact, different demand on the reader.
// ============================================================================

import { usablesLabel } from '../../shared/facility-billing';
import { fractionToFeePct } from './mgmt-fee';

/** The stages a card can sit at where nobody is expected to act any more. */
const SETTLED_STAGES = new Set(['Completed', 'Cancelled']);

export type AnswerTone = 'answered' | 'action' | 'muted';

export interface IntakeChip {
  /** Short form for a Kanban card / table cell. */
  text: string;
  /** Long form for a title attribute — says what the state means. */
  title: string;
  tone: AnswerTone;
}

export type IntakeRow = {
  stage?: string | null;
  management_fee_pct?: number | null;
  tcs_provides_usables?: boolean | null;
};

/**
 * True when an unanswered question on this card is history rather than a task.
 * Keyed on stage, not on status: a Stalled card in the pipeline still needs its
 * answers, and its stage is still a pipeline stage.
 */
export function isSettled(row: IntakeRow): boolean {
  return SETTLED_STAGES.has(row.stage ?? '');
}

function missingTone(row: IntakeRow): AnswerTone {
  return isSettled(row) ? 'muted' : 'action';
}

/** Management fee, as a chip. NULL is a gap; 0 is an answer. */
export function feeChip(row: IntakeRow): IntakeChip {
  const v = row.management_fee_pct;
  if (v == null) {
    return {
      text: 'Fee not answered',
      title: isSettled(row)
        ? 'Management fee was never recorded on this card. It predates the intake field — the live figure is on the facility.'
        : 'Management fee not answered. Set it on the Pricing & Billing tab before this card advances.',
      tone: missingTone(row),
    };
  }
  if (v === 0) {
    return {
      text: 'Fee 0%',
      title: 'No management fee — recorded deliberately, not missing.',
      tone: 'answered',
    };
  }
  return {
    text: `Fee ${fractionToFeePct(v)}%`,
    title: `Management fee ${fractionToFeePct(v)}%.`,
    tone: 'answered',
  };
}

/** Usables, as a chip. Never says "No" for an unknown (AFB-0041). */
export function usablesChip(row: IntakeRow): IntakeChip {
  const v = row.tcs_provides_usables;
  if (v == null) {
    return {
      text: 'Usables not answered',
      title: isSettled(row)
        ? 'Nobody recorded whether TCS supplies usables. It predates the intake field — the live answer is on the facility.'
        : 'Usables not answered. Set it on the Pricing & Billing tab before this card advances.',
      tone: missingTone(row),
    };
  }
  return {
    text: v ? 'Usables: TCS' : 'Usables: client',
    title: usablesLabel(v),
    tone: 'answered',
  };
}

/**
 * The intake answers still outstanding, named the way a person would say them.
 * Empty array = nothing outstanding.
 *
 * The screen's early warning for the Phase 6b stage gate. The database gate is
 * the authority (item 830, Ben 2026-10-12): once a building is linked it reads
 * ONLY the building's answer; before that, the card's intake answer. The row
 * passed here comes from the card view, which falls back to the card's copy
 * where the building is blank, so in that one case the screen lets the move
 * through and the database refuses it with its own sentence. No open card is
 * in that case (2026-10-14).
 */
export function missingIntakeAnswers(row: IntakeRow): string[] {
  const out: string[] = [];
  if (row.management_fee_pct == null) out.push('management fee %');
  if (row.tcs_provides_usables == null) out.push('usables');
  return out;
}

/**
 * The one transition Phase 6b gates: out of the first stage, where the BDD's
 * involvement ends and the centralized call begins.
 *
 * The brief asked for a block "past the BDD stage". There is no BDD stage — the
 * live domain is Info_and_Sub_Checklist -> Centralized_Confirmation_Call ->
 * Executive_Review -> Final_Pre_Invoicing_Check -> Completed (+ Cancelled,
 * Stalled), and the data anchor the brief named, bdd_completed_date, is NULL on
 * ALL 36 rows. BDD work is the stage_1_bdd checklist cluster, which
 * CHECKLIST_STAGE_TO_DB_STAGE maps to Info_and_Sub_Checklist — so leaving that
 * stage is the earliest transition after BDD involvement, and it is the one
 * gated. Ben's call, 2026-08-18.
 */
export const GATED_FROM_STAGE = 'Info_and_Sub_Checklist';
export const GATED_TO_STAGE = 'Centralized_Confirmation_Call';

/**
 * The gate itself. Returns the reason to block, or null to allow.
 *
 * Only the one forward transition is gated. Backward moves, Cancelled, Stalled
 * and every later stage pass: this exists to stop a card carrying an unanswered
 * question INTO the centralized call, not to trap one that is already past it.
 */
export function blockedStageMoveReason(
  row: IntakeRow,
  nextStage: string,
): string | null {
  if (row.stage !== GATED_FROM_STAGE || nextStage !== GATED_TO_STAGE) return null;
  const missing = missingIntakeAnswers(row);
  if (missing.length === 0) return null;

  const where = missing.length > 1 ? 'Both are' : "It's";
  // Only mention the zero escape hatch when the fee is what is missing. A gate
  // whose message explains a field the reader is not being asked about reads as
  // boilerplate, and boilerplate is what people learn to click past.
  const zeroCounts = missing.includes('management fee %')
    ? ' If there is genuinely no fee, record 0% — that is an answer.'
    : '';
  return (
    `Answer ${missing.join(' and ')} before the centralized confirmation call. ` +
    `${where} on the Pricing & Billing tab.${zeroCounts}`
  );
}
